import { useEffect, useState, useCallback } from 'react';
import { Mail, MessageCircle, Send, Info, ChevronRight, ChevronDown, X, FileText } from 'lucide-react';
import { news as newsApi } from '../utils/api';
import { clienti as clientiApi } from '../utils/api';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

const CATEGORIE = [
  { key: '', label: 'Tutte' },
  { key: 'normativa', label: 'Normativa' },
  { key: 'fiscale', label: 'Fiscale' },
  { key: 'mercato', label: 'Mercato' },
  { key: 'sicurezza', label: 'Sicurezza' },
  { key: 'prezzi', label: 'Prezzi' },
];

const CATEGORIA_COLORS = {
  normativa: 'var(--zone-acqua)',
  fiscale: 'var(--brand)',
  mercato: 'var(--warn)',
  sicurezza: 'var(--ds-danger)',
  prezzi: 'var(--ds-muted)',
};

const CTA_MAP = {
  preventiv: { label: 'Vai ai preventivi', path: '/preventivi' },
  fattur: { label: 'Controlla le fatture', path: '/fatture' },
  cantier: { label: 'Vedi i cantieri', path: '/cantieri' },
  fornitor: { label: 'Gestisci fornitori', path: '/fornitori' },
  client: { label: 'Anagrafica clienti', path: '/clienti' },
  scadenz: { label: 'Controlla le scadenze', path: '/fatture' },
  iva: { label: 'Verifica IVA', path: '/governance' },
  bonus: { label: 'Approfondisci', path: '/governance' },
  detrazi: { label: 'Approfondisci', path: '/governance' },
};

function getCtas(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  const found = [];
  for (const [keyword, cta] of Object.entries(CTA_MAP)) {
    if (lower.includes(keyword) && !found.some(f => f.path === cta.path)) {
      found.push(cta);
    }
  }
  return found.slice(0, 2);
}

const fmtData = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

function SkeletonCard() {
  return (
    <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: '20px 24px', boxShadow: CARD_SHADOW }}>
      <div style={{ height: 16, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 12, width: '65%', opacity: 0.5 }} />
      <div style={{ height: 10, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 8, width: '90%', opacity: 0.4 }} />
      <div style={{ height: 120, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 12, opacity: 0.3 }} />
      <div style={{ height: 10, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', width: '70%', opacity: 0.4 }} />
    </div>
  );
}

// ── Modal Invia ai Clienti ──────────────────────────────────────────────────

function InviaModal({ articolo, onClose, onSuccess }) {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [canale, setCanale] = useState('email');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    clientiApi.list({ page_size: 200 })
      .then(res => {
        const data = res.data;
        setClienti(Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []);
      })
      .catch(() => setClienti([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredClienti = clienti.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (c.nome || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  const toggleClient = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const validIds = filteredClienti
      .filter(c => canale === 'whatsapp' ? c.telefono : c.email)
      .map(c => c.id);
    setSelected(validIds);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await newsApi.invia(articolo.id, { client_ids: selected, canale });
      setResult({ success: true, message: res.data.detail });
      onSuccess?.();
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || 'Errore nell\'invio' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: 520, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--ds-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>
              Invia ai clienti
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <X size={18} strokeWidth={2} color="var(--ds-muted)" />
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 4 }}>
            {articolo.titolo}
          </div>
        </div>

        {/* Canale */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ds-border)' }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Canale di invio
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { key: 'email', label: 'Email', icon: <Mail size={14} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> },
              { key: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle size={14} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> },
              { key: 'entrambi', label: 'Entrambi', icon: <Send size={14} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> },
            ].map(ch => (
              <button
                key={ch.key}
                onClick={() => { setCanale(ch.key); setSelected([]); }}
                style={{
                  padding: '8px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500,
                  border: canale === ch.key ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
                  background: canale === ch.key ? tint('var(--brand)', 12) : 'var(--ds-bg)',
                  color: canale === ch.key ? 'var(--brand)' : 'var(--ds-text)',
                  cursor: 'pointer',
                }}
              >
                {ch.icon} {ch.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search + Select All */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--ds-border)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="Cerca cliente..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-base)', fontSize: 13,
              border: '1px solid var(--ds-border)', background: 'var(--chip)',
              color: 'var(--ds-text)', outline: 'none',
            }}
          />
          <button onClick={selectAll} style={{
            padding: '8px 12px', borderRadius: 'var(--radius-base)', fontSize: 12, fontWeight: 500,
            background: 'var(--chip)', border: '1px solid var(--ds-border)',
            color: 'var(--ds-text)', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            Seleziona tutti
          </button>
        </div>

        {/* Lista clienti */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px', maxHeight: 280 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>
              Caricamento clienti...
            </div>
          ) : filteredClienti.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>
              Nessun cliente trovato
            </div>
          ) : filteredClienti.map(c => {
            const hasChannel = canale === 'whatsapp' ? !!c.telefono : canale === 'email' ? !!c.email : (!!c.email || !!c.telefono);
            return (
              <label
                key={c.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                  borderBottom: '1px solid var(--ds-border)',
                  opacity: hasChannel ? 1 : 0.4, cursor: hasChannel ? 'pointer' : 'not-allowed',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={() => hasChannel && toggleClient(c.id)}
                  disabled={!hasChannel}
                  style={{ width: 16, height: 16, accentColor: 'var(--brand)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ds-text)' }}>{c.nome}</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>
                    {canale === 'whatsapp' ? (c.telefono || 'Telefono mancante') : (c.email || 'Email mancante')}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--ds-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {result ? (
            <div style={{
              fontSize: 13, fontWeight: 500, flex: 1,
              color: result.success ? 'var(--ok)' : 'var(--ds-danger)',
            }}>
              {result.message}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>
              {selected.length} clienti selezionati
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '10px 20px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500,
              background: 'var(--chip)', border: '1px solid var(--ds-border)',
              color: 'var(--ds-text)', cursor: 'pointer',
            }}>
              {result ? 'Chiudi' : 'Annulla'}
            </button>
            {!result && (
              <button
                onClick={handleSend}
                disabled={selected.length === 0 || sending}
                style={{
                  padding: '10px 20px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500,
                  background: selected.length === 0 ? 'var(--ds-border)' : 'var(--brand)',
                  border: 'none', color: '#fff', cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: sending ? 0.7 : 1,
                }}
              >
                {sending ? 'Invio in corso...' : `Invia a ${selected.length} clienti`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Componente principale ───────────────────────────────────────────────────

export default function Novita() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoria, setCategoria] = useState('');
  const [mese, setMese] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [inviaArticolo, setInviaArticolo] = useState(null);

  const loadFeed = useCallback(() => {
    setLoading(true);
    const params = {};
    if (categoria) params.categoria = categoria;
    if (mese) params.mese = mese;
    newsApi.feed(params)
      .then(res => {
        const data = res.data;
        setItems(Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [categoria, mese]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const filtered = items;

  const pillStyle = (active) => ({
    fontSize: 13, fontWeight: 500, padding: '10px 16px', borderRadius: 'var(--radius-base)', cursor: 'pointer',
    border: active ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
    background: active ? 'var(--brand)' : 'var(--ds-bg)',
    color: active ? '#fff' : 'var(--ds-text)',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 26, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)', margin: 0, lineHeight: 1.2 }}>
          Novita del Settore
        </h1>
        <p style={{ fontSize: 14, color: 'var(--ds-muted)', margin: '5px 0 0' }}>
          Aggiornamenti, normative e novita dal settore edile
        </p>
      </div>

      {/* Filtri categoria + mese */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
        {CATEGORIE.map(c => (
          <button key={c.key} onClick={() => setCategoria(c.key)} style={pillStyle(categoria === c.key)}>
            {c.label}
          </button>
        ))}
        <div style={{ width: 1, height: 24, background: 'var(--ds-border)', margin: '0 4px' }} />
        <input
          type="month"
          value={mese}
          onChange={e => setMese(e.target.value)}
          style={{
            fontSize: 13, fontWeight: 400, padding: '8px 12px', borderRadius: 'var(--radius-base)', cursor: 'pointer',
            border: mese ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
            background: mese ? tint('var(--brand)', 12) : 'var(--ds-bg)',
            color: mese ? 'var(--brand)' : 'var(--ds-text)',
            outline: 'none',
          }}
        />
        {mese && (
          <button
            onClick={() => setMese('')}
            style={{
              fontSize: 12, fontWeight: 400, padding: '8px 12px', borderRadius: 'var(--radius-base)',
              border: '1px solid var(--ds-border)', background: 'var(--ds-bg)',
              color: 'var(--ds-muted)', cursor: 'pointer',
            }}
          >
            Azzera
          </button>
        )}
      </div>

      {/* Contenuto */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <FileText size={48} strokeWidth={1.5} color="var(--ds-border)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 6 }}>
            {categoria ? 'Nessuna novita in questa categoria' : 'Nessuna novita per ora'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Torna presto!</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(item => {
            const color = CATEGORIA_COLORS[item.categoria] || 'var(--ds-muted)';
            const catLabel = CATEGORIE.find(c => c.key === item.categoria)?.label || item.categoria;
            const isExpanded = expandedId === item.id;
            const hasBody = item.corpo && item.corpo.trim();
            const ctas = getCtas(item.suggerimento_ai);

            return (
              <article
                key={item.id}
                style={{
                  background: 'var(--ds-bg)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--ds-border)',
                  overflow: 'hidden',
                  boxShadow: CARD_SHADOW,
                }}
              >
                {/* Card clickable header */}
                <div
                  onClick={() => hasBody && setExpandedId(isExpanded ? null : item.id)}
                  style={{ padding: '20px 24px', cursor: hasBody ? 'pointer' : 'default' }}
                >
                  {/* Titolo + categoria */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                    <h2 style={{
                      fontSize: 18, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)',
                      lineHeight: 1.4, margin: 0, flex: 1,
                    }}>
                      {item.titolo}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 99,
                        background: tint(color, 14), color,
                      }}>
                        {catLabel}
                      </span>
                      {hasBody && (
                        <ChevronDown
                          size={16} strokeWidth={2} color="var(--ds-muted)"
                          style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Sommario */}
                  {item.sommario && (
                    <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, margin: '0 0 8px' }}>
                      {item.sommario}
                    </p>
                  )}

                  {/* Meta */}
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>
                    {fmtData(item.data_pubblicazione)}
                    {item.fonte_nome && <span> · {item.fonte_nome}</span>}
                    {hasBody && !isExpanded && (
                      <span style={{ color: 'var(--brand)', marginLeft: 8 }}>Leggi tutto</span>
                    )}
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ padding: '0 24px 20px' }}>
                    {/* Immagine */}
                    {item.immagine_url && (
                      <div style={{ marginBottom: 16 }}>
                        <img
                          src={item.immagine_url}
                          alt={item.titolo}
                          style={{
                            width: '25%', borderRadius: 'var(--radius-base)',
                            aspectRatio: '16/9', objectFit: 'cover',
                          }}
                        />
                      </div>
                    )}

                    {/* Corpo */}
                    {hasBody && (
                      <div style={{
                        fontSize: 14, color: 'var(--ds-text)', lineHeight: 1.7,
                        whiteSpace: 'pre-wrap', marginBottom: 16,
                      }}>
                        {item.corpo}
                      </div>
                    )}

                    {/* Suggerimento AI */}
                    {item.suggerimento_ai && (
                      <div style={{
                        background: tint('var(--brand)', 6), border: '1px solid ' + tint('var(--brand)', 25),
                        borderRadius: 'var(--radius-card)', padding: '16px 18px', marginBottom: 12,
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                        }}>
                          <Info size={18} strokeWidth={2} color="var(--brand)" />
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand)' }}>
                            Consiglio per la tua attivita
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--ds-text)', lineHeight: 1.6 }}>
                          {item.suggerimento_ai}
                        </div>

                        {/* CTA buttons */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                          {ctas.map((cta, i) => (
                            <a
                              key={i}
                              href={cta.path}
                              style={{
                                padding: '8px 14px', borderRadius: 'var(--radius-base)', fontSize: 12, fontWeight: 500,
                                background: 'var(--ds-bg)', border: '1px solid ' + tint('var(--brand)', 25),
                                color: 'var(--brand)', textDecoration: 'none', cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}
                            >
                              {cta.label}
                              <ChevronRight size={12} strokeWidth={2} />
                            </a>
                          ))}
                          <button
                            onClick={(e) => { e.stopPropagation(); setInviaArticolo(item); }}
                            style={{
                              padding: '8px 14px', borderRadius: 'var(--radius-base)', fontSize: 12, fontWeight: 500,
                              background: 'var(--brand)', border: 'none',
                              color: '#fff', cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <Send size={14} strokeWidth={2} />
                            Invia ai clienti
                          </button>
                        </div>
                      </div>
                    )}

                    {/* CTA invia anche senza suggerimento AI */}
                    {!item.suggerimento_ai && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setInviaArticolo(item); }}
                          style={{
                            padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500,
                            background: 'var(--brand)', border: 'none',
                            color: '#fff', cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                          }}
                        >
                          <Send size={14} strokeWidth={2} />
                          Invia ai clienti
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Modal Invia */}
      {inviaArticolo && (
        <InviaModal
          articolo={inviaArticolo}
          onClose={() => setInviaArticolo(null)}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
